import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import uuid from 'short-unique-id';
import { MentionComponent } from './mention.component';
import { AngularRenderer } from 'ngx-tiptap';
import { Injector } from '@angular/core';
import { AppInjector } from 'app/app.module';
import { DomHelperService } from 'app/global/dom-helper.service';
import { translate } from '@jsverse/transloco';
const generatedUuid = new uuid({ length: 6, dictionary: 'number' });

const items = () => [
  { id: generatedUuid(), label: `[${translate("Content new field (Template/Document - section) - Field name - New field")}]`, type: 'textfield', name: 'Text field' },
  { id: generatedUuid(), label: `[New field]`, type: 'datepicker', name: 'Datepicker' },
  { id: generatedUuid(), label: `[New field]`, type: 'droplist', name: 'Droplist' }
];

const updateAttributes = (node:Element) => {
  let attr = {};
  if(node){
    attr = {
      id: node?.getAttribute('uuid'),
      type: node?.getAttribute('type'),
      value: node?.querySelector('input')?.getAttribute('value'),
      style: node?.querySelector('.selected-label')?.getAttribute('style'),
      class: node?.getAttribute('class'),
      label: node?.querySelector('.selected-label')?.textContent,
      textTagEditor: node?.querySelector('input')?.getAttribute('class'),
    }
  }

  return attr;
}

// Configure the Mention extension with Tippy.js
export const mention = Mention.configure({
  deleteTriggerWithBackspace: true,
  renderHTML({ node }) {
    let attrs = node.attrs;
    const drafFieldSelector = document.querySelector('.template-page')?.querySelector(`.drag-fields[uuid="${node.attrs.id}"]`);
    if(drafFieldSelector){
      attrs = updateAttributes(drafFieldSelector);
    }
    if (node.attrs.type == "textfield") {
      return [
        'span',
        {
          contenteditable: 'false',
          class: attrs.class,
          field: 'text-field',
          field_required: '1',
          field_style: '1',
          "data-type": "mention",
          "data-label": attrs.label,
          "data-id": attrs.id,
          uuid: attrs.id,
          type: attrs.type
        },
        [
          'span',
          { class: 'tag is-info tag-container' },
          [
            'span',
            {
              class: 'selected-label text-tag',
              style: attrs.style,
            },
            `${attrs.label}`, // Display mention label
          ],
          ['input', { type: 'text', class: attrs.textTagEditor, value: attrs.value }],
        ],
      ];
    } else if (node.attrs.type == "datepicker") {
      return [
        'span',
        {
          contenteditable: 'false',
          class: attrs.class,
          field: 'datepicker-field',
          field_required: '1',
          field_style: '1',
          "data-type": "mention",
          "data-label": attrs.label,
          "data-id": attrs.id,
          uuid: attrs.id,
          type: attrs.type
        },
        [
          'span',
          { class: 'tag is-info tag-container' },
          [
            'span',
            {
              class: 'selected-label tag-date',
              style: attrs.style,
            },
            `${attrs.label}`, // Display mention label
          ]
        ],
      ];
    } else if (node.attrs.type == "droplist") {
      return ['span',
        {
          contenteditable: 'false',
          class: attrs.class,
          field: 'droplist-field',
          field_required: '1',
          "data-type": "mention",
          "data-label": attrs.label,
          "data-id": attrs.id,
          uuid: attrs.id,
          type: attrs.type,
        },
        [
          'span',
          { class: 'tag is-info tag-container' },
          [
            'span',
            {
              class: 'drag-fields-inner-container droplist-field-inner-container droplist-menu',
            },
            [
              'span',
              {
                class: 'selected-label tag-droplist selected-option-label',
                style: attrs.style,
              },
              `${attrs.label}`, // Display text for the label
            ],
            [
              'span',
              { class: 'hidden', id: attrs.id }, // Unique identifier span
              '',
            ]
          ],
        ],
      ];
    }
  },
  HTMLAttributes: {
    class: 'mention',
  },
  suggestion: {
    char: '[',
    allowSpaces: true,
    items: async ({ query }) => {
      // Filter based on the query
      return items().filter((item: { name: string, id: number }) => {
        return item.name.toLowerCase().includes(query.toLowerCase())
      });
    },
    render: () => {
      let renderer: AngularRenderer<MentionComponent, MentionComponent>;
      let popup;
      const injector = AppInjector.get(Injector);
      return {
        onStart: (props) => {
          renderer = new AngularRenderer(MentionComponent, injector, {
            _props:props,
          });
          renderer.updateProps({ _props:props });
          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: renderer.dom,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            onHide() {
              renderer.destroy();
            },
          });
        },

        onUpdate(props) {
          renderer.updateProps({ _props:props });

          popup[0].setProps({
            getReferenceClientRect: props.clientRect,
          });
        },
        onKeyDown(props) {
          return renderer.instance.onKeyDown({...props});
        },
        onExit: () => {
          popup[0].destroy();
          renderer.destroy();
        },
      };
    },
  },
}).extend({
  onUpdate() {
    const domHelperService = AppInjector.get(DomHelperService);
    domHelperService.initializeEventListener('.open-tag-popover', 'click', 'openTagPopover', {role_id:0, hide_settings:false});
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      uuid: {
        default: null,
        parseHTML: element => element.getAttribute('id'),
        renderHTML: attributes => attributes.id,
      },
      type: {
        default: null,
        parseHTML: element => element.getAttribute('type'),
        renderHTML: attributes => attributes.type,
      },
      value: {
        default: null,
        parseHTML: element => element.querySelector('input')?.getAttribute('value'),
        renderHTML: attributes => attributes.value,
      },
      style: {
        default: 'color:#28c76f;',
        parseHTML: (element: HTMLElement) => {
          return element.querySelector('.selected-label').getAttribute('style');
        },
        renderHTML: attributes => {
          return {
            style: attributes.style,
          };
        },
      },
      class: {
        default: 'tags drag-fields open-tag-popover',
        parseHTML: (element: HTMLElement) => {
          // Get the attribute value from markup if it's available, otherwise assign null as the attribute value
          return element.getAttribute('class');
        },
        renderHTML: attributes => {
          return {
            class: attributes.class,
          };
        },
      },
      label: {
        default: '[New field]',
        parseHTML: (element: HTMLElement) => {
          // Get the attribute value from markup if it's available, otherwise assign null as the attribute value
          return element.querySelector('.selected-label')?.textContent;
        },
        renderHTML: attributes => {
          return {
            label: attributes.label,
          };
        },
      },
      textTagEditor: {
        default : 'form-control text-tag-editor hidden',
        parseHTML: (element: HTMLElement) => {
          return element.querySelector('input')?.getAttribute('class')
        },
        renderHTML: attributes => {
          return {
            textTagEditor: attributes.textTagEditor,
          };
        },
      }
    }
  },
});